/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ToolUsageClientSummary,
  ToolUsageClientSummary$inboundSchema,
} from "./toolusageclientsummary.js";
import {
  ToolUsageClientToolBreakdownRow,
  ToolUsageClientToolBreakdownRow$inboundSchema,
} from "./toolusageclienttoolbreakdownrow.js";
import {
  ToolUsageTargetSummary,
  ToolUsageTargetSummary$inboundSchema,
} from "./toolusagetargetsummary.js";
import {
  ToolUsageTargetTimeSeriesPoint,
  ToolUsageTargetTimeSeriesPoint$inboundSchema,
} from "./toolusagetargettimeseriespoint.js";
import {
  ToolUsageTargetToolBreakdownRow,
  ToolUsageTargetToolBreakdownRow$inboundSchema,
} from "./toolusagetargettoolbreakdownrow.js";
import {
  ToolUsageTotals,
  ToolUsageTotals$inboundSchema,
} from "./toolusagetotals.js";
import {
  ToolUsageUsersByTargetRow,
  ToolUsageUsersByTargetRow$inboundSchema,
} from "./toolusageusersbytargetrow.js";
import {
  ToolUsageUserSummary,
  ToolUsageUserSummary$inboundSchema,
} from "./toolusageusersummary.js";
import {
  ToolUsageUserTimeSeriesPoint,
  ToolUsageUserTimeSeriesPoint$inboundSchema,
} from "./toolusageusertimeseriespoint.js";

/**
 * Target-aware MCP and tool usage metrics
 */
export type GetToolUsageSummaryResult = {
  /**
   * Per-tool usage rows grouped by MCP client
   */
  clientToolBreakdown: Array<ToolUsageClientToolBreakdownRow>;
  /**
   * Top MCP clients for the selected filters and time range
   */
  clients: Array<ToolUsageClientSummary>;
  /**
   * Time-series usage buckets grouped by target
   */
  targetTimeSeries: Array<ToolUsageTargetTimeSeriesPoint>;
  /**
   * Per-tool usage rows grouped by target
   */
  targetToolBreakdown: Array<ToolUsageTargetToolBreakdownRow>;
  /**
   * Top usage targets for the selected filters and time range
   */
  targets: Array<ToolUsageTargetSummary>;
  /**
   * Target-aware MCP and tool usage totals
   */
  totals: ToolUsageTotals;
  /**
   * Time-series usage buckets grouped by user identity
   */
  userTimeSeries: Array<ToolUsageUserTimeSeriesPoint>;
  /**
   * Top user identities for the selected filters and time range
   */
  users: Array<ToolUsageUserSummary>;
  /**
   * Cross-dimensional usage rows grouped by target and user identity
   */
  usersByTarget: Array<ToolUsageUsersByTargetRow>;
};

/** @internal */
export const GetToolUsageSummaryResult$inboundSchema: z.ZodMiniType<
  GetToolUsageSummaryResult,
  unknown
> = z.pipe(
  z.object({
    client_tool_breakdown: z.array(
      ToolUsageClientToolBreakdownRow$inboundSchema,
    ),
    clients: z.array(ToolUsageClientSummary$inboundSchema),
    target_time_series: z.array(ToolUsageTargetTimeSeriesPoint$inboundSchema),
    target_tool_breakdown: z.array(
      ToolUsageTargetToolBreakdownRow$inboundSchema,
    ),
    targets: z.array(ToolUsageTargetSummary$inboundSchema),
    totals: ToolUsageTotals$inboundSchema,
    user_time_series: z.array(ToolUsageUserTimeSeriesPoint$inboundSchema),
    users: z.array(ToolUsageUserSummary$inboundSchema),
    users_by_target: z.array(ToolUsageUsersByTargetRow$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_tool_breakdown": "clientToolBreakdown",
      "target_time_series": "targetTimeSeries",
      "target_tool_breakdown": "targetToolBreakdown",
      "user_time_series": "userTimeSeries",
      "users_by_target": "usersByTarget",
    });
  }),
);

export function getToolUsageSummaryResultFromJSON(
  jsonString: string,
): SafeParseResult<GetToolUsageSummaryResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => GetToolUsageSummaryResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'GetToolUsageSummaryResult' from JSON`,
  );
}
